import { zodResolver } from "@hookform/resolvers/zod";
import { ListOrdered, Pencil } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { ActionCell } from "@/components/shared/action-cell";
import { ConfigFormDialog } from "@/components/shared/config-form-dialog";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type CapabilityScaleRow,
  useCapabilityScale,
  useRenameCapabilityLevel,
  useSetCapabilityLevelVisibility,
} from "@/hooks/use-capability-levels";
import { type LevelNameInput, levelNameSchema } from "@/schemas/capability-levels";

/**
 * Settings → Config → Performance ratings: rename the rungs of the global scale
 * shared by every skill and competency, and choose where each is offered. The
 * set of rungs is seeded — names and visibility are editable, rungs are never
 * added, removed, or reordered.
 *
 * Called "Levels" until WI-103; renamed so "level" means the career ladder
 * (Associate/Staff/Senior/…) and "rating" means this scale. The route path,
 * the `capability_level` table and every audit diff key are deliberately
 * unchanged — this was a labelling change only.
 */
export function SettingsLevelsPage() {
  const { data: scale = [], isLoading, isError, error, refetch } = useCapabilityScale();
  const [editing, setEditing] = useState<CapabilityScaleRow | null>(null);

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load performance ratings"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted-foreground">
        The scale every skill and competency is rated against — five graded ratings plus{" "}
        <span className="font-medium text-foreground">N/A</span>. Each rating's definition shows on
        hover wherever ratings appear; edits apply everywhere immediately.{" "}
        <span className="font-medium text-foreground">Shown in app</span> controls whether a rating
        appears in the app at all — the Person Profile and each role's expectation grid;{" "}
        <span className="font-medium text-foreground">Feedback survey</span> controls whether it can
        be picked on the survey. Hiding a rating never deletes anything already written against it.
        What a rating means for a specific capability is written per role and career level, on that
        role's Required capabilities tab — the same skill is described differently by different
        roles. This is separate from{" "}
        <span className="font-medium text-foreground">Role levels</span>, the career ladder a role
        opts into.
      </p>

      <DataTable
        headers={[
          { label: "Rating" },
          { label: "Name" },
          { label: "Definition" },
          { label: "Shown in app", className: "text-center" },
          { label: "Feedback survey", className: "text-center" },
          { label: "Actions", className: "text-right" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState
            icon={ListOrdered}
            title="No performance ratings"
            description="The scale is seeded on server startup."
          />
        }
      >
        {scale.map((level) => (
          <tr
            key={level.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
          >
            <td className="px-4 py-3 text-muted-foreground">{level.ordinal}</td>
            <td className="px-4 py-3 font-medium">{level.name}</td>
            <td className="max-w-md px-4 py-3 text-sm text-muted-foreground">
              {level.description ? (
                <span className="line-clamp-2">{level.description}</span>
              ) : (
                <span className="italic text-muted-foreground/70">No definition yet.</span>
              )}
            </td>
            <VisibilityCell
              level={level}
              field="showInApp"
              label={`Show ${level.name} in the app`}
            />
            <VisibilityCell
              level={level}
              field="showOnFeedback"
              label={`Show ${level.name} on the feedback survey`}
            />
            <td className="px-4 py-3 text-right">
              <ActionCell
                items={[{ icon: Pencil, label: "Edit", onClick: () => setEditing(level) }]}
              />
            </td>
          </tr>
        ))}
      </DataTable>

      {editing && (
        <RenameLevelDialog
          key={editing.id}
          open
          onOpenChange={() => setEditing(null)}
          level={editing}
        />
      )}
    </div>
  );
}

/**
 * One visibility checkbox, saved on click — there's no Save button for these,
 * so the toggle IS the commit. `useSetCapabilityLevelVisibility` updates the
 * row optimistically and rolls back on failure.
 *
 * Sends only the flag it owns, so ticking one box can't write the other's value
 * back over a change made elsewhere.
 */
function VisibilityCell({
  level,
  field,
  label,
}: {
  level: CapabilityScaleRow;
  field: "showInApp" | "showOnFeedback";
  label: string;
}) {
  const setVisibility = useSetCapabilityLevelVisibility();
  return (
    <td className="px-4 py-3 text-center">
      <Checkbox
        checked={level[field]}
        aria-label={label}
        onCheckedChange={(next) =>
          setVisibility.mutate({ ordinal: level.ordinal, [field]: next === true })
        }
      />
    </td>
  );
}

function RenameLevelDialog({
  open,
  onOpenChange,
  level,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level: CapabilityScaleRow;
}) {
  const rename = useRenameCapabilityLevel();
  const form = useForm<LevelNameInput>({
    resolver: zodResolver(levelNameSchema),
    defaultValues: { ordinal: level.ordinal, name: level.name, description: level.description },
  });

  async function onSubmit(values: LevelNameInput) {
    await rename.mutateAsync(values);
    onOpenChange(false);
  }

  return (
    <ConfigFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={<>Edit rating {level.ordinal}</>}
      description={
        <>
          The name and definition apply to every skill and competency that uses the scale. The
          definition shows on hover wherever ratings appear.
        </>
      }
      form={form}
      onSubmit={onSubmit}
      submitLabel="Save"
      pending={rename.isPending}
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Meets Expectations" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Definition</FormLabel>
            <FormControl>
              <Textarea
                rows={4}
                placeholder="What this rating means, in general, for any skill or competency…"
                {...field}
                value={field.value ?? ""}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </ConfigFormDialog>
  );
}
